import React from 'react';
import { Field, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';

import { Bok } from 'lib/templates/Foundation.BOKF.model';
import { Marketing } from 'lib/templates/Feature.BOKF.model';

export type PageItemProps = {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    PageTitle?: Field<string>;
    MetaDescription?: Field<string>;
    PageCategory?: Marketing.DataTemplates.ContentCategory;
    PageType?: Marketing.DataTemplates.ContentType;
  };
};

type CategoryKey =
  | 'recommended-category-1'
  | 'recommended-category-2'
  | 'recommended-category-3'
  | 'recommended-category-4'
  | 'recommended-category-5'
  | 'recommended-category-6';

const categoryStylesMap = {
  'recommended-category-1': {
    border: 'border border-light-other-redVibrant',
    hover: 'hover:outline hover:outline-light-other-redVibrant',
    bg: 'bg-light-other-redVibrant3/60',
    text: 'text-light-other-redVibrant2',
  },
  'recommended-category-2': {
    border: 'border border-light-other-orange',
    hover: 'hover:outline hover:outline-light-other-orange',
    bg: 'bg-light-other-orangeWarm3/60',
    text: 'text-light-other-orangeWarm2',
  },
  'recommended-category-3': {
    border: 'border border-light-other-plum',
    hover: 'hover:outline hover:outline-light-other-plum',
    bg: 'bg-light-other-plum3/60',
    text: 'text-light-other-plum2',
  },
  'recommended-category-4': {
    border: 'border border-light-other-burgundy4',
    hover: 'hover:outline hover:outline-light-other-burgundy4',
    bg: 'bg-light-other-burgundy3/40',
    text: 'text-light-other-burgundy2',
  },
  'recommended-category-5': {
    border: 'border border-light-stroke-secondary-brand',
    hover: 'hover:outline hover:outline-light-stroke-secondary-brand',
    bg: 'bg-light-other-red/50',
    text: 'text-light-fill-tertiary-brand',
  },
  'recommended-category-6': {
    border: 'border border-light-other-violet2',
    hover: 'hover:outline hover:outline-light-other-violet2',
    bg: 'bg-light-other-violet3/60',
    text: 'text-light-other-violet2',
  },
};

const getCategoryStyles = (page: PageItemProps) => {
  const categoryColor = (page.fields?.PageCategory?.fields?.Color as Bok.BaseTypes.Selector)?.fields
    ?.Value?.value;

  return (
    categoryStylesMap[categoryColor as CategoryKey] || categoryStylesMap['recommended-category-6']
  );
};

const RecommendedContent = (props: Marketing.DataTemplates.RecommendedContent): JSX.Element => {
  const items = props.fields?.RecommendedContentPages as PageItemProps[];

  if (!items || items.length === 0) {
    return <></>;
  }

  return (
    <section className={`container mt-12 recommended-content`}>
      <div className="flex flex-col gap-y-4">
        <div className="flex items-center gap-x-1">
          <ImageWrapper field={props?.fields?.RecommendedContentTitleImage} />
          <Text field={props?.fields?.RecommendedContentTitle} tag="p" className="p3-medium" />
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-[22px] flex-col @3xl:flex-row">
          {items.length > 0
            ? items.map((page) => {
                const styles = getCategoryStyles(page);

                return (
                  <LinkWrapper
                    key={page.id}
                    className={`recommended-content-card flex ${styles.border} ${styles.hover}`}
                    field={{
                      value: {
                        href: page.url,
                      },
                    }}
                  >
                    <ImageWrapper
                      field={page.fields?.PageCategory?.fields?.Image}
                      className="min-w-[100px] h-fit self-center recommended-content-image"
                    />
                    <div className="flex flex-col gap-y-2 recommended-content-content">
                      <div className="flex items-center gap-x-2 recommended-content-group">
                        <Text
                          field={page.fields?.PageCategory?.fields?.Name}
                          tag="p"
                          className={`c1-medium ${styles.text} px-2 py-1.5 recommended-category ${styles.bg} rounded-md w-fit`}
                        />
                        <Text
                          field={page.fields?.PageType?.fields?.Name}
                          tag="p"
                          className={`recommended-category-type c1-medium uppercase`}
                        />
                      </div>
                      <Text
                        field={page.fields?.PageTitle}
                        tag="p"
                        className={`p2-medium text-light-fill-primary recommended-page-title`}
                      />
                      <Text
                        field={page.fields?.MetaDescription}
                        tag="p"
                        className={`p3 text-light-fill-primary-variant recommended-page-description`}
                      />
                    </div>
                  </LinkWrapper>
                );
              })
            : null}
        </div>
      </div>
    </section>
  );
};

export default RecommendedContent;
